import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FileType, Download, Loader } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { whatsappMediaService } from '@/services/whatsappMediaService';
import { FormattedMessageContent } from '@/components/chat/FormattedMessageContent';
import type { Message } from '@/types/message-types';

interface MediaDocumentProps {
  message: Message;
  tenantId: string;
}

export function MediaDocument({ message, tenantId }: MediaDocumentProps) {
  const { t } = useTranslation('conversations');
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getFileName = () => {
    // First check metadata for filenames - prioritize original_filename
    if (message.metadata?.original_filename) return message.metadata.original_filename;
    if (message.metadata?.filename) return message.metadata.filename;
    if (message.metadata?.file_name) return message.metadata.file_name;
    
    // Extract filename from media_url if available
    if (message.media_url) {
      const urlParts = message.media_url.split('/');
      const filename = urlParts[urlParts.length - 1];
      if (filename && filename.includes('.')) {
        // Try to extract original filename by removing timestamp prefix
        const decodedFilename = decodeURIComponent(filename);
        const parts = decodedFilename.split('-');
        if (parts.length > 1) {
          // Remove timestamp prefix (first part) and rejoin
          return parts.slice(1).join('-');
        }
        return decodedFilename;
      }
    }
    
    // Fallback to generic names, but avoid showing [DOCUMENT]
    if (message.content && message.content !== '[DOCUMENT]') {
      return message.content;
    }
    
    return 'document.pdf';
  };

  const getCaption = () => {
    if (message.content && message.content !== '[DOCUMENT]') {
      return message.content;
    }
    return null;
  };

  const getFileSize = () => {
    const size = message.metadata?.file_size;
    if (!size) return '';
    
    const kb = size / 1024;
    const mb = kb / 1024;
    
    if (mb >= 1) {
      return `${mb.toFixed(1)} MB`;
    } else {
      return `${kb.toFixed(0)} KB`;
    }
  };

  useEffect(() => {
    const loadDocument = async () => {
      // Debug logging for customer media issue
      console.log('📄 MediaDocument loadDocument:', {
        messageId: message.id,
        senderType: message.sender_type,
        hasMediaUrl: !!message.media_url,
        mediaUrlValue: message.media_url,
        mediaUrlLength: message.media_url?.length,
        metadata: message.metadata
      });

      // If we have a direct media URL, resolve storage paths to signed URLs
      if (message.media_url?.trim()) {
        const resolved = await whatsappMediaService.resolveMediaUrl(message.media_url);
        if (resolved) {
          console.log('✅ MediaDocument: Using resolved media_url');
          setDocumentUrl(resolved);
          setLoading(false);
          return;
        }
      }

      // Otherwise, try to extract WhatsApp media ID for customer documents
      console.log('📥 MediaDocument: Attempting to extract media_id from metadata');
      const mediaId = whatsappMediaService.extractMediaIdFromMessage(message);
      console.log('🔍 MediaDocument: Extracted media_id:', mediaId);

      if (!mediaId) {
        console.error('❌ MediaDocument: No media ID found in message:', {
          messageId: message.id,
          metadata: message.metadata,
          media_url: message.media_url
        });
        setError('No media available');
        setLoading(false);
        return;
      }

      try {
        const result = await whatsappMediaService.downloadAndGetMediaUrl(
          tenantId,
          mediaId,
          'document',
          message.id
        );

        if (result.success) {
          setDocumentUrl(result.fileUrl || null);
        } else {
          setError(result.error || 'Failed to load document');
        }
      } catch (err) {
        setError('Failed to load document');
      } finally {
        setLoading(false);
      }
    };

    loadDocument();
  }, [message, tenantId]);

  if (loading) {
    return (
      <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
        <Loader className="h-5 w-5 animate-spin" />
        <div>
          <div className="text-sm font-medium">{t('media.loadingDocument')}</div>
          <div className="text-xs text-muted-foreground">{getFileName()}</div>
        </div>
      </div>
    );
  }

  const handleDownload = async () => {
    if (documentUrl) {
      // Refresh signed URL before download (may be expired after long session)
      let url = documentUrl;
      if (message.media_url) {
        const fresh = await whatsappMediaService.getFreshSignedUrl(message.media_url);
        if (fresh) url = fresh;
      }
      const link = document.createElement('a');
      link.href = url;
      link.download = getFileName();
      link.click();
    }
  };

  const caption = getCaption();

  return (
    <div className="max-w-sm">
      {caption && (
        <FormattedMessageContent content={caption} className="text-sm mb-2 px-1" />
      )}
      <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
        <div className="flex-shrink-0">
          <div className="relative">
            <FileType className="h-8 w-8 text-red-600" />
            <div className="absolute -bottom-1 -right-1 bg-red-600 text-white text-xs px-1 rounded text-[10px] font-medium">
              PDF
            </div>
          </div>
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">
            {getFileName()}
          </div>
          <div className="text-xs text-muted-foreground">
            {getFileSize()}
            {error && <span className="text-destructive ml-2">• {error}</span>}
          </div>
        </div>
        
        {documentUrl && (
          <Button
            size="sm"
            variant="ghost"
            onClick={handleDownload}
            className="h-8 w-8 p-0 flex-shrink-0"
          >
            <Download className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}